import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ShieldCheck, ExternalLink, Loader2, AlertTriangle, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Yearly renewal page. Authenticated — the user hits this when their access has
 * lapsed (or when they want to extend proactively). Same flow as the initial
 * activation: send 150 USDT → paste TX hash → paid_until is extended by 365
 * days. Reachable at /renew.
 */
export default function Renew() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [cfgError, setCfgError] = useState("");
  const [copied, setCopied] = useState("");
  const [txHash, setTxHash] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/payment/renewal-config")
      .then((r) => setCfg(r.data))
      .catch((e) => {
        setCfg(false);
        setCfgError(formatApiError(e));
      });
  }, []);

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/payment/verify-renewal", { tx_hash: txHash.trim() });
      setVerified(data);
      toast.success("Renewal confirmed on-chain");
      await refresh();
      setTimeout(() => navigate("/", { replace: true }), 1600);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return iso;
    }
  };

  if (cfg === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-0 text-zinc-500 text-sm tracking-widest uppercase">
        Loading renewal
      </div>
    );
  }

  if (cfg === false) {
    return (
      <div className="min-h-screen bg-ink-0 text-white flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-red-400 mb-3">Renewal unavailable</p>
          <p className="text-sm text-zinc-500 mb-6">{cfgError}</p>
          <button onClick={() => navigate("/login")} className="text-sm text-gold hover:text-gold-hover">Go to sign in</button>
        </div>
      </div>
    );
  }

  const isExpired = cfg.is_expired;
  const daysLeft = cfg.days_left;

  return (
    <div className="min-h-screen bg-ink-0 text-white relative">
      <div className="absolute top-6 right-6 z-10 flex items-center gap-3">
        <LanguageSwitcher />
        <button
          onClick={logout}
          className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-1.5"
          data-testid="renew-logout"
        >
          <LogOut className="w-3.5 h-3.5" /> Log out
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-6 pt-16 pb-24">
        <div className="flex items-baseline gap-2 mb-12">
          <span className="font-display text-2xl font-semibold">EM</span>
          <span className="font-display text-2xl font-light text-gold">Operating System</span>
        </div>

        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3" data-testid="renew-eyebrow">
          {isExpired ? "Access expired — renew to continue" : "Extend your access"}
        </p>
        <h1 className="font-display text-4xl font-light tracking-tight mb-3">
          {isExpired ? (
            <>Time to <span className="text-gold">renew</span>.</>
          ) : (
            <>Extend for <span className="text-gold">another year</span>.</>
          )}
        </h1>
        <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-md">
          Your current window {isExpired ? "ended" : "runs until"}{" "}
          <span className="text-white font-semibold">{fmtDate(cfg.paid_until)}</span>
          {!isExpired && typeof daysLeft === "number" && (
            <> — <span className="text-white font-semibold">{daysLeft} day{daysLeft === 1 ? "" : "s"}</span> left</>
          )}
          . Send another <span className="text-white font-semibold">{cfg.amount_usdt} USDT</span> on the{" "}
          <span className="text-white font-semibold">Ethereum (ERC-20)</span> network to keep everything unlocked for another year.
        </p>

        {user?.email && (
          <p className="text-[11px] text-zinc-600 mb-8">Signed in as <span className="text-zinc-400">{user.email}</span></p>
        )}

        {verified ? (
          <div className="em-card p-8 text-center" data-testid="renew-verified">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 mx-auto mb-4 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl font-light tracking-tight mb-2">
              Renewal confirmed<span className="text-gold">.</span>
            </h2>
            <p className="text-sm text-zinc-400 mb-1">{verified.amount_usdt} USDT received</p>
            <p className="text-xs text-zinc-600 mb-2">Valid until {fmtDate(verified.paid_until)}</p>
            <p className="text-xs text-zinc-600 mb-6">Redirecting you back to the dashboard&hellip;</p>
            <Loader2 className="w-5 h-5 animate-spin text-gold mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="em-card p-6">
              <div className="bg-white rounded-2xl p-5 mb-4 flex items-center justify-center">
                <QRCodeSVG value={cfg.wallet_address} size={200} level="M" includeMargin={false} data-testid="renew-qr" />
              </div>
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Amount</div>
                <div className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
                  <span data-testid="renew-amount" className="font-mono text-lg text-white">{cfg.amount_usdt} USDT</span>
                  <button
                    type="button"
                    onClick={() => copy(cfg.amount_usdt, "amount")}
                    className="text-zinc-500 hover:text-gold"
                    aria-label="Copy amount"
                  >
                    {copied === "amount" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Wallet address · ERC-20</div>
                <div className="bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <code data-testid="renew-address" className="text-xs text-white font-mono break-all leading-relaxed">{cfg.wallet_address}</code>
                    <button
                      type="button"
                      onClick={() => copy(cfg.wallet_address, "address")}
                      className="shrink-0 text-zinc-500 hover:text-gold mt-0.5"
                      data-testid="renew-copy-address"
                      aria-label="Copy address"
                    >
                      {copied === "address" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex items-start gap-2 text-[11px] text-amber-200/70 leading-relaxed">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
                <span>Only send <strong className="text-amber-200">USDT on the Ethereum (ERC-20) network</strong>. Other networks (BSC, Polygon, TRON) will be lost.</span>
              </div>
            </div>

            <div className="em-card p-6 flex flex-col">
              <h3 className="font-display text-xl font-light tracking-tight mb-2">Confirm your renewal</h3>
              <p className="text-xs text-zinc-500 leading-relaxed mb-5">
                After sending, paste your <strong className="text-zinc-300">transaction hash</strong>. We verify it on-chain and extend your access by 12 months.
              </p>
              <form onSubmit={submit} className="space-y-4 flex-1 flex flex-col" data-testid="renew-form">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Transaction hash</label>
                  <input
                    data-testid="renew-tx-input"
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    placeholder="0x… — 66-character Ethereum transaction hash"
                    className="em-input font-mono text-xs"
                    required
                    minLength={64}
                    maxLength={68}
                    autoComplete="off"
                  />
                  <a
                    href={txHash.length >= 60 ? `https://etherscan.io/tx/${txHash.trim().startsWith("0x") ? txHash.trim() : "0x" + txHash.trim()}` : "https://etherscan.io"}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-[11px] text-zinc-500 hover:text-gold"
                  >
                    <ExternalLink className="w-3 h-3" /> View on Etherscan
                  </a>
                </div>
                {err && (
                  <div className="text-xs text-red-400 bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2" data-testid="renew-error">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting || txHash.trim().length < 60}
                  data-testid="renew-verify-btn"
                  className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  <span>{submitting ? "Verifying on-chain…" : "Verify renewal"}</span>
                </button>
                <p className="text-[11px] text-zinc-600 leading-relaxed mt-auto">
                  Ethereum needs ~12 confirmations (~3 minutes). If verification says &ldquo;waiting for confirmations&rdquo;, wait a moment and try again — your hash stays valid.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
